import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table, TableColumnsType, Tag } from 'antd'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useCounterpartServiceDeleteCounterpartEndpoint,
  useCounterpartServiceGetCounterpartListEndpoint,
  useCounterpartServiceGetCounterpartListEndpointKey
} from 'src/api/queries'
import { ApiError, InstitutionStatus } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  DeleteConfirm,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons,
  Text
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

export const CounterParts = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()
  const theme = useTheme()

  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const queryParams = {
    pageSize: DEFAULT_PER_PAGE,
    includeTheLastSignedContract: true,
    pageNumber: Number(pagination.current),
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useCounterpartServiceGetCounterpartListEndpoint(queryParams)

  const { mutate } = useCounterpartServiceDeleteCounterpartEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetCounterpartListEndpointKey]
      })
      message.success(t('counterparts.counter_part_deleted'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const getActionItems = useMemo(
    () => (id: string) => {
      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => {
            navigate(paths.counterpart.details(String(id)))
          }
        },
        {
          key: 'delete',
          label: (
            <DeleteConfirm
              title={t('counterparts.delete_counterpart')}
              onConfirm={() => mutate({ id })}
            />
          ),
          danger: true
        }
      ]
    },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'name',
        title: t('counterparts.producer_name'),
        dataIndex: 'name'
      },
      {
        key: 'contract_number',
        title: t('counterparts.contract_number'),
        render: ({ lastSignedContract }) => (
          <Flex vertical>
            <Text weight={400}>
              #{lastSignedContract?.contractNumber ?? '-'}
            </Text>
            <Text weight={400} color={theme.colorTextTertiary}>
              {t('counterparts.signed_date')}:{' '}
              {dayjs(lastSignedContract?.signedOn).format(
                dayMonthYearBackSlash
              )}
            </Text>
          </Flex>
        )
      },
      {
        key: 'active_from',
        title: t('counterparts.active_from'),
        render: ({ lastSignedContract }) =>
          lastSignedContract?.startDate
            ? dayjs(lastSignedContract?.startDate).format(dayMonthYearBackSlash)
            : '-'
      },
      {
        key: 'active_to',
        title: t('counterparts.active_to'),
        render: ({ lastSignedContract }) =>
          lastSignedContract?.endDate
            ? dayjs(lastSignedContract?.endDate).format(dayMonthYearBackSlash)
            : '-'
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ status }) => {
          const isActive = status === InstitutionStatus.ACTIVE

          return (
            <Tag color={isActive ? 'success' : 'error'}>
              {isActive ? t('common.active') : t('common.inactive')}
            </Tag>
          )
        }
      },
      {
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: ({ id }) => (
          <TableActionButtons justify="center" items={getActionItems(id)} />
        )
      }
    ],
    [t, theme, getActionItems]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={t('counterparts.title')}
      items={[{ title: t('counterparts.title') }]}
    >
      <Flex justify="space-between">
        <SearchBar
          placeholder={t('common.search')}
          onSearch={(val) => onSearch(val)}
          maxWidth={260}
        />
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => navigate(paths.counterpart.add)}
        >
          {t('counterparts.add_counterpart')}
        </Button>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
